import { useCallback } from 'react'
import { useSessionStore } from '@/stores'

/**
 * Capability keys the server may report. Mirrors `CapabilitiesResponse`.
 *
 * A capability is a fact about the DEPLOYMENT; a permission is a fact about
 * the PERSON. Both are needed to render a control, and they fail in different
 * ways when confused: gating on the permission alone offers a button the
 * environment cannot honour (a 503), and gating on the capability alone offers
 * one the API will refuse (a 403).
 */
export const CAPABILITIES = {
  /**
   * Can this deployment dispatch a recorder for a pasted meeting link?
   *
   * False wherever `RECORDER_TASK_*` is unwired, which includes every
   * environment that has not applied the ECS half of the manual recording
   * work. The button must be hidden there, not merely disabled: there is
   * nothing the person can do about it.
   */
  manualRecording: 'manualRecording',
  /**
   * Can this deployment take audio recorded in the browser ("Record now")?
   * Off unless `DEVICE_RECORDING_ENABLED` is set on the API.
   */
  deviceRecording: 'deviceRecording',
  /**
   * Can this deployment take product feedback ("Share feedback")?
   * Off unless `FEEDBACK_ENABLED` is set on the API.
   */
  feedback: 'feedback',
  /**
   * Is the Contact Support FORM offered, rather than a mailto link? Off unless
   * `SUPPORT_FORM_ENABLED` is set on the API and the transport wired. Unlike
   * the two above, false does not always hide the entry: the menu falls back to
   * a mailto link IF the API names a fallback address (`supportEmail`), and
   * offers nothing when it does not.
   */
  supportForm: 'supportForm',
} as const

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES]

/**
 * What this deployment can do, for deciding which components to render.
 *
 * FAILS CLOSED, exactly as `usePermissions` does and for the same reason: the
 * map is null until the session has been established, and an unknown key reads
 * as false. A control that appears during that window and then vanishes reads
 * as a bug in the product rather than as a loading state.
 *
 * THIS IS COSMETIC. The route re-checks: an unwired deployment answers
 * `503 recording_dispatch_unavailable` whatever the browser believes.
 */
export function useCapabilities() {
  const capabilities = useSessionStore((s) => s.capabilities)

  const can = useCallback(
    // Only an exact `true` is a capability: the map also carries values that
    // are not booleans (`supportEmail`), and a truthy string is not a "yes".
    (capability: Capability) => capabilities?.[capability] === true,
    [capabilities],
  )

  return {
    /** Null while unknown. Prefer `can`, which treats null as unavailable. */
    capabilities,
    /** True once the map is known, so a caller can show a skeleton instead. */
    loaded: capabilities !== null,
    can,
  }
}

/**
 * The public support address the API offers when the form cannot be used, or
 * null. Null is the safe state, not a failure: nothing in the repository
 * establishes that any mailbox is monitored, so the SPA offers no email until
 * the deployment names one (`SUPPORT_FALLBACK_EMAIL`). It is not a recipient of
 * anything the form sends.
 */
export function useSupportEmail(): string | null {
  const value = useSessionStore((s) => s.capabilities?.supportEmail)
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

/** Single-capability convenience, for the common `{can && <Button/>}` case. */
export function useCapability(capability: Capability): boolean {
  return useCapabilities().can(capability)
}
